
import React from 'react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Checkbox } from '@/components/ui/checkbox';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { AlertTriangle } from 'lucide-react';
import { Alert, AlertTitle, AlertDescription } from '@/components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

interface AlertConfigurationProps {
  editing: boolean;
}

const AlertConfiguration: React.FC<AlertConfigurationProps> = ({ editing }) => {
  return (
    <div className="space-y-4">
      <h3 className="text-lg font-medium">Alert Configuration</h3>
      
      <Alert variant="default" className="bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-700">
        <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-400" />
        <AlertTitle className="text-amber-700 dark:text-amber-400">Alert System</AlertTitle>
        <AlertDescription className="text-amber-600 dark:text-amber-400">
          Configure how alerts are generated and delivered across the system
        </AlertDescription>
      </Alert>
      
      <div className="space-y-4">
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label htmlFor="alerts-enabled" className="text-base">Enable Alerts</Label>
            <p className="text-sm text-muted-foreground">Turn on/off the entire alert system</p>
          </div>
          <Switch id="alerts-enabled" defaultChecked disabled={!editing} />
        </div>
        
        <div className="space-y-2">
          <Label className="text-base">Alert Generation Frequency</Label>
          <RadioGroup defaultValue="realtime" className="pt-2" disabled={!editing}>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="realtime" id="realtime" />
              <Label htmlFor="realtime" className="cursor-pointer">Real-time</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="hourly" id="hourly" />
              <Label htmlFor="hourly" className="cursor-pointer">Hourly</Label>
            </div>
            <div className="flex items-center space-x-2">
              <RadioGroupItem value="daily" id="daily" />
              <Label htmlFor="daily" className="cursor-pointer">Daily</Label>
            </div>
          </RadioGroup>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-2">
          <div className="space-y-2">
            <Label htmlFor="critical-threshold">Critical Alert Threshold (%)</Label>
            <Input 
              id="critical-threshold" 
              type="number" 
              placeholder="90" 
              defaultValue="90" 
              readOnly={!editing} 
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="warning-threshold">Warning Alert Threshold (%)</Label>
            <Input 
              id="warning-threshold" 
              type="number" 
              placeholder="70" 
              defaultValue="70" 
              readOnly={!editing} 
            />
          </div>
        </div>
        
        <div className="space-y-2">
          <Label className="text-base">Alert Types</Label>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 pt-2">
            <div className="flex items-center space-x-2">
              <Checkbox id="system-alerts" defaultChecked disabled={!editing} />
              <Label htmlFor="system-alerts" className="cursor-pointer">System Issues</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox id="device-alerts" defaultChecked disabled={!editing} />
              <Label htmlFor="device-alerts" className="cursor-pointer">Device Status</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox id="maintenance-alerts" defaultChecked disabled={!editing} />
              <Label htmlFor="maintenance-alerts" className="cursor-pointer">Maintenance Schedules</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox id="usage-alerts" defaultChecked disabled={!editing} />
              <Label htmlFor="usage-alerts" className="cursor-pointer">Abnormal Usage</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox id="scada-alerts" defaultChecked disabled={!editing} />
              <Label htmlFor="scada-alerts" className="cursor-pointer">SCADA Connectivity</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox id="security-alerts" defaultChecked disabled={!editing} />
              <Label htmlFor="security-alerts" className="cursor-pointer">Security Events</Label>
            </div>
          </div>
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="alert-retention">Alert Retention Period (days)</Label>
          <Input 
            id="alert-retention" 
            type="number" 
            placeholder="30" 
            defaultValue="30" 
            readOnly={!editing} 
          />
          <p className="text-xs text-muted-foreground">How long alerts will be stored in the system before automatic deletion</p>
        </div>
        
        <div className="space-y-2 pt-2">
          <Label htmlFor="alert-escalation">Alert Escalation Path</Label>
          <Select disabled={!editing} defaultValue="standard">
            <SelectTrigger id="alert-escalation">
              <SelectValue placeholder="Select escalation path" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="standard">Standard (Email → SMS → Phone)</SelectItem>
              <SelectItem value="urgent">Urgent (All channels simultaneously)</SelectItem>
              <SelectItem value="gradual">Gradual (15min intervals between channels)</SelectItem>
              <SelectItem value="custom">Custom Configuration</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
    </div>
  );
};

export default AlertConfiguration;
